<template>
  <div class="mx-3 ma-4">
    <v-card class="pa-5">
      <v-card-title>Customer Invoices</v-card-title>

      <!-- Page Title with Search -->
      <PageTitleWithSearch
        :title="'Invoices'"
        :btnName="'Add New Invoice'"
        :btnColor="'#00B3FF'"
        :btnRoute="'/invoices/create'"
        :btnPermission="'invoice.create'"
        :btnEnable="false"
        :loading="loading"
        :search="searchQuery"
        @searchText="searchChange($event)"
      />

      <!-- Invoices Table -->
      <v-data-table
        v-if="!loading && invoices.length"
        :headers="headers"
        :items="displayedInvoices"
        :items-per-page="10"
        :hide-default-footer="true"
        :disable-sort="true"
        :disable-pagination="true"
      >
        <template v-slot:item="{ item, index }">
          <tr :class="index % 2 === 0 ? 'dark-row' : 'light-row'" class="custom-row">
            <td class="text-left">{{ item.invoice_number }}</td>
            <td class="text-left">{{ item.invoice_date }}</td>
            <td class="text-left">{{ item.invoice_due_date }}</td>
            <td class="text-left">{{ currencyCode }}{{ formatNumber(item.total_invoice) }}</td>
            <td class="text-left">{{ currencyCode }}{{ formatNumber(item.pay_amount) }}</td>
            <td class="text-left" :class="dueAmountClass(item.out_standing_amount)">{{ currencyCode }}{{ formatNumber(item.out_standing_amount) }}</td>
            <td class="text-left">
              <v-chip :color="statusColor(item)" small>{{ statusLabel(item) }}</v-chip>
            </td>
          </tr>
        </template>
      </v-data-table>

      <!-- View More Button -->
      <v-btn
        v-if="invoices.length > displayedInvoices.length"
        color="primary"
        @click="loadMoreInvoices"
        block
      >
        View More
      </v-btn>
    </v-card>

    <!-- Pagination -->
    <Pagination 
      @previous="page--" 
      @next="page++" 
      :loading="loading" 
      :total="totalInvoices" 
      :page="page" 
      class="pt-5"
    />

    <!-- Snackbar Notifications -->
    <Snackbars 
      :text="successMsg" 
      :snackbar="snackbar" 
      :color="'warning'" 
      @classSnackbar="closeSnackbar" 
    />
  </div>
</template>

<style scoped>
.router-link {
  text-decoration: none;
}
.dark-row {
  background-color: #fafafa;
  color: rgb(0, 0, 0);
}
.light-row {
  background-color: #ffffff;
}
.custom-row {
  height: 60px;
  border: none;
}
</style>

<script>
import { createNamespacedHelpers } from "vuex";
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import Pagination from "@/components/shared/Pagination";
import Snackbars from "@/components/shared/Snackbars";
import { CustomerService } from "@/services/customer-service.js";

const { mapGetters } = createNamespacedHelpers("auth");
const { mapGetters: mapSettingsGetters } = createNamespacedHelpers("settings");

export default {
  name: "CustomerInvoices",
  components: { PageTitleWithSearch, Pagination, Snackbars },
  data() {
    return {
      invoices: [],
      displayedInvoices: [],
      searchQuery: "",
      page: 1,
      perPage: 10,
      loading: false,
      totalInvoices: 0,
      successMsg: "Invoice operation successful!",
      snackbar: false,
      headers: [
        { text: "Invoice Number", value: "invoice_number", align: "start" },
        { text: "Date", value: "invoice_date", align: "start" },
        { text: "Due Date", value: "invoice_due_date", align: "start" },
        { text: "Amount", value: "total_invoice", align: "start" },
        { text: "Paid Amount", value: "pay_amount", align: "start" },
        { text: "Due Amount", value: "out_standing_amount", align: "start" },
        { text: "Status", value: "status", align: "start" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      customerId: "profile/customer_id",
      hasPermission: "hasPermission",
    }),
    ...mapSettingsGetters(["currencyCode"]),
  },
  methods: {
    async fetchInvoices() {
      const uuid = this.$route.params.uuid;
      this.loading = true;
      try {
        const response = await CustomerService.getInvoices(uuid, this.page, this.perPage, this.searchQuery);
        
        if (this.page === 1) {
          this.invoices = response.invoices;
        } else {
          this.invoices = [...this.invoices, ...response.invoices];
        }

        this.totalInvoices = response.total;
        this.updateDisplayedInvoices();
      } catch (error) {
        console.error("Error fetching invoices:", error);
      } finally {
        this.loading = false;
      }
    },
    updateDisplayedInvoices() {
      this.displayedInvoices = this.invoices.slice(0, this.page * this.perPage);
    },
    loadMoreInvoices() {
      this.page++;
      this.updateDisplayedInvoices();
    },
    searchChange(searchValue) {
      this.searchQuery = searchValue;
      this.page = 1;
      this.fetchInvoices();
    },
    closeSnackbar() {
      this.snackbar = false;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    dueAmountClass(amount) {
      return Number(amount) > 0 ? 'red--text' : 'green--text';
    },
    statusLabel(item) {
      if (Number(item.out_standing_amount) <= 0) return 'Completed';
      const dueDate = new Date(item.invoice_due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) return 'Overdue';
      return 'Pending';
    },
    statusColor(item) {
      if (Number(item.out_standing_amount) <= 0) return 'success';
      const dueDate = new Date(item.invoice_due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dueDate < today) return 'error';
      return 'warning';
    },
  },
  watch: {
    page() {
      this.fetchInvoices();
    },
  },
  mounted() {
    this.fetchInvoices();
  },
};
</script>
